import type { Position } from 'components/map/types';
import { RioPositionSchema } from 'helper/zod';

import { errorHandler } from './errors';

import type { VideoStreamingError } from 'API';

type WithVin = { vin: string };
type WithId = { id: string };
type WithIdentifier = WithId | WithVin;
export type GenericRecord<T extends WithIdentifier> = Record<string, T>;

export const normalizeRecordData = <T extends WithIdentifier>(data?: GenericRecord<T>): T[] => {
    if (!data) {
        return [];
    }
    return Object.values(data);
};

export const denormalizeListWithVin = <T extends WithVin>(list: T[]): GenericRecord<T> =>
    list.reduce((pre: GenericRecord<T>, cur) => ({ ...pre, [cur.vin]: cur }), {});

export const denormalizeListWithId = <T extends WithId>(list: T[]): GenericRecord<T> =>
    list.reduce((pre: GenericRecord<T>, cur) => ({ ...pre, [cur.id]: cur }), {});

export const createVideoStreamingErrorIdentifier = ({ vin, cameraID }: VideoStreamingError) => `${vin}:${cameraID}`;

/**
 * Generic fetch utility for V2 GraphQL queries.
 * Executes the query, parses the response with the provided parse function,
 * and handles errors uniformly via errorHandler.
 *
 * @param query - A function that executes the GraphQL query and returns the raw data
 * @param parse - A function that parses the raw data into the desired result type
 * @param errorMessage - The error message passed to errorHandler on failure
 */
export const fetchV2 = async <TData, TResult>(
    query: () => Promise<{ data: TData }>,
    parse: (data: TData) => TResult,
    errorMessage: string
): Promise<{ data: TResult }> => {
    try {
        const { data } = await query();
        return { data: parse(data) };
    } catch (error) {
        throw errorHandler(errorMessage, error);
    }
};

export const buildReverseGeocodeParams = (position: Position | undefined) => {
    if (!position) {
        return;
    }

    const response = RioPositionSchema().safeParse(position);
    if (!response.success) {
        console.error('[buildReverseGeocodeParams]: the Position is not defined accordingly', {
            position,
            error: response.error,
        });
        return;
    }
    const { lat, lng } = response.data;
    return `${lat},${lng}`;
};
